import { X } from "lucide-react";
import { PICKS, fmtDate, fmtPts } from "../lib/api";
import { Team } from "./TeamRow";

const PickButton = ({ active, color, disabled, onClick, children, testId }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    data-testid={testId}
    className={`pick-btn h-11 md:h-10 rounded-lg border font-display text-lg font-bold touch-manipulation disabled:opacity-40 disabled:cursor-not-allowed ${
      active ? `${color} text-[#0A0D14] shadow-lg` : "border-[#26334D] bg-[#121824] text-slate-300 hover:border-slate-500"
    }`}
  >
    {children}
  </button>
);

const ScoreInput = ({ value, onChange, disabled, testId }) => (
  <input
    type="number" inputMode="numeric" pattern="[0-9]*" min={0} max={20} disabled={disabled} value={value ?? ""}
    onChange={(e) => onChange(e.target.value === "" ? null : Math.max(0, Math.min(20, Number(e.target.value))))}
    data-testid={testId}
    className="w-12 h-10 rounded-lg bg-[#121824] border border-[#26334D] text-center font-display text-xl focus:outline-none focus:border-violet-500 disabled:opacity-40"
  />
);

const Result = ({ r }) => (
  <span className={`text-xs font-mono px-2 py-1 rounded-full ${r.points > 0 || r.correct ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`} data-testid="match-result-badge">
    {r.points != null ? `+${fmtPts(r.points)} pt` : r.correct ? "Indovinato" : "Sbagliato"}
    {r.exact ? " · esatto" : ""}
  </span>
);

export const MatchCard = ({ m, pred, onChange, index }) => {
  const locked = !m.is_open;
  const tid = `match-${m.match_id}`;
  const setPick = (pick_type, pick) =>
    pred?.pick === pick ? onChange(null) : onChange({ exact_home: null, exact_away: null, ...pred, pick_type, pick });
  const setExact = (k, v) => pred && onChange({ ...pred, [k]: v });
  const finished = m.score_home != null;

  return (
    <div className={`card-surface p-4 sm:p-5 fade-up ${pred ? "border-emerald-500/40" : ""}`} style={{ animationDelay: `${index * 30}ms` }} data-testid={`${tid}-card`}>
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{fmtDate(m.utc_date)}</span>
        {m.result ? <Result r={m.result} /> : ["IN_PLAY", "PAUSED"].includes(m.status) ? (
          <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase px-2 py-1 rounded-full bg-red-500/15 text-red-400" data-testid={`${tid}-live`}>
            <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" /> {m.status === "PAUSED" ? "Intervallo" : "Live"}
          </span>
        ) : ["POSTPONED", "SUSPENDED"].includes(m.status) ? (
          <span className="text-[10px] font-mono uppercase px-2 py-1 rounded-full bg-orange-500/15 text-orange-300" data-testid={`${tid}-postponed`}>Rinviata</span>
        ) : locked && <span className="text-[10px] font-mono uppercase text-slate-500">Chiusa</span>}
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 sm:gap-3 mb-3 sm:mb-5">
        <Team team={m.home} />
        <span className={`font-display text-2xl font-black ${["IN_PLAY", "PAUSED"].includes(m.status) ? "text-red-400" : finished ? "text-white" : "text-slate-600"}`} data-testid={`${tid}-score`}>
          {finished ? `${m.score_home} - ${m.score_away}` : "VS"}
        </span>
        <Team team={m.away} align="right" />
      </div>
      <div className="grid grid-cols-6 md:grid-cols-3 gap-1.5 sm:gap-2">
        {PICKS.fissa.map((p) => (
          <PickButton key={p} active={pred?.pick === p} color="bg-emerald-500 border-emerald-500 shadow-emerald-500/30" disabled={locked} onClick={() => setPick("fissa", p)} testId={`${tid}-fissa-${p}`}>{p}</PickButton>
        ))}
        {PICKS.doppia.map((p) => (
          <PickButton key={p} active={pred?.pick === p} color="bg-blue-500 border-blue-500 shadow-blue-500/30" disabled={locked} onClick={() => setPick("doppia", p)} testId={`${tid}-doppia-${p}`}>{p}</PickButton>
        ))}
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 sm:mt-4 sm:pt-4 border-t border-[#26334D]">
        <span className="text-[10px] sm:text-xs font-mono uppercase tracking-widest text-violet-400">Risultato esatto</span>
        <div className="flex items-center gap-2">
          <ScoreInput value={pred?.exact_home} disabled={locked || !pred} onChange={(v) => setExact("exact_home", v)} testId={`${tid}-exact-home`} />
          <span className="text-slate-500">-</span>
          <ScoreInput value={pred?.exact_away} disabled={locked || !pred} onChange={(v) => setExact("exact_away", v)} testId={`${tid}-exact-away`} />
          {pred && !locked && (
            <button onClick={() => onChange(null)} className="ml-1 h-10 w-8 grid place-items-center text-slate-500 hover:text-red-400" data-testid={`${tid}-clear`} title="Rimuovi pronostico"><X size={18} /></button>
          )}
        </div>
      </div>
    </div>
  );
};
